import React, { useState, useEffect, useCallback } from 'react';
import './GuidedAnimalNumbers.css';

interface PowerTableRow {
  perGroup: number;
  effectSize: number;
  total: number;
}

interface AnalysisData {
  groupCount: number;
  species: string;
  studyType: string;
  explicitNumbers: number | null;
}

interface SuggestionData {
  mice: number;
  rats: number;
  otherSpecies: boolean;
  perGroup: number;
  attritionBuffer: string;
}

interface StructuredData {
  analysis: AnalysisData;
  suggestion: SuggestionData;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
  assumptions: string[];
  justificationText: string;
}

interface GuidedAnimalNumbersProps {
  protocolId: number;
  apiBaseUrl: string;
  getAuthHeaders: () => Record<string, string>;
  onAccepted: (data: {
    mice: number;
    rats: number;
    otherSpecies: boolean;
    justificationText: string;
    reconciliationNote?: string;
  }) => void;
  onError: (error: string) => void;
}

const GuidedAnimalNumbers: React.FC<GuidedAnimalNumbersProps> = ({
  protocolId,
  apiBaseUrl,
  getAuthHeaders,
  onAccepted,
  onError
}) => {
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  // Structured data from backend
  const [structured, setStructured] = useState<StructuredData | null>(null);
  const [powerTable, setPowerTable] = useState<PowerTableRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  // Editable fields
  const [miceCount, setMiceCount] = useState(0);
  const [ratsCount, setRatsCount] = useState(0);
  const [otherSpecies, setOtherSpecies] = useState(false);
  const [justificationText, setJustificationText] = useState('');

  // Reconciliation state
  const [originalNumbers, setOriginalNumbers] = useState<number | null>(null);
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [reconciliationNote, setReconciliationNote] = useState('');
  const [includeReconciliation, setIncludeReconciliation] = useState(true);

  // Generate animal numbers analysis on mount
  const generateAnalysis = useCallback(async () => {
    try {
      setIsGenerating(true);
      setParseError(null);

      const response = await fetch(`${apiBaseUrl}/api/guided-conversation/${protocolId}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          phaseNumber: 3
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate animal numbers analysis');
      }

      const data = await response.json();

      // Check if we got structured data
      if (data.isStructured && data.structured) {
        setStructured(data.structured);
        setPowerTable(data.powerTable || []);

        // Pre-fill editable fields from suggestion
        const suggestion = data.structured.suggestion;
        const suggestedMice = suggestion?.mice || 0;
        const suggestedRats = suggestion?.rats || 0;
        setMiceCount(suggestedMice);
        setRatsCount(suggestedRats);
        setOtherSpecies(suggestion?.otherSpecies || false);
        setJustificationText(data.structured.justificationText || data.content || '');

        // Track original numbers from experimental design
        const original = data.structured.analysis?.explicitNumbers;
        setOriginalNumbers(original);

        // Check if numbers differ and set up reconciliation
        const totalSuggested = suggestedMice + suggestedRats;
        if (original !== null && original !== totalSuggested) {
          setShowReconciliation(true);
          setReconciliationNote(
            `Note: Animal numbers were refined during protocol development based on power analysis and study-specific considerations. Original design specified ${original} animals; final count adjusted to ${totalSuggested} (see Q4.3 for detailed justification).`
          );
        }
      } else {
        // Fallback to text-only mode
        setParseError(data.parseError || 'Received unstructured response');
        setJustificationText(data.content || '');
      }

    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to generate analysis');
    } finally {
      setIsGenerating(false);
      setIsLoading(false);
    }
  }, [apiBaseUrl, protocolId, getAuthHeaders, onError]);

  useEffect(() => {
    generateAnalysis();
  }, [generateAnalysis]);

  // Handle accept
  const handleAccept = () => {
    if (!justificationText.trim()) {
      onError('Please provide justification text');
      return;
    }

    onAccepted({
      mice: miceCount,
      rats: ratsCount,
      otherSpecies,
      justificationText: justificationText.trim(),
      reconciliationNote: (showReconciliation && includeReconciliation) ? reconciliationNote.trim() : undefined
    });
  };

  // Check if current numbers differ from original (for dynamic reconciliation)
  const currentTotal = miceCount + ratsCount;
  const numbersChanged = originalNumbers !== null && originalNumbers !== currentTotal;

  // Confidence badge color
  const getConfidenceBadgeClass = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'confidence-high';
      case 'medium': return 'confidence-medium';
      case 'low': return 'confidence-low';
      default: return 'confidence-medium';
    }
  };

  if (isLoading || isGenerating) {
    return (
      <div className="animal-numbers">
        <div className="animal-numbers-loading">
          <div className="loading-spinner"></div>
          <p>Analyzing experimental design and calculating animal numbers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animal-numbers">
      <div className="animal-numbers-header">
        <h3>Animal Numbers Analysis</h3>
        <p>Review the suggested animal numbers based on your experimental design. You can edit any values before accepting.</p>
      </div>

      {/* Power Analysis Reference Table */}
      {powerTable.length > 0 && structured && (
        <div className="power-table-section">
          <div className="an-section-header">
            <h4>Power Analysis Reference</h4>
            <span className="power-note">80% power, alpha=0.05</span>
          </div>
          <table className="power-table">
            <thead>
              <tr>
                <th>Animals/Group</th>
                <th>Detectable Effect</th>
                <th>Total ({structured.analysis.groupCount} groups)</th>
              </tr>
            </thead>
            <tbody>
              {powerTable.map((row, idx) => (
                <tr
                  key={idx}
                  className={structured.suggestion.perGroup === row.perGroup ? 'highlighted' : ''}
                >
                  <td>{row.perGroup}</td>
                  <td>{row.effectSize} SD</td>
                  <td>{row.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Suggested Numbers */}
      <div className="suggestion-section">
        <div className="an-section-header">
          <h4>Suggested Animal Numbers</h4>
          {structured?.confidence && (
            <span className={`confidence-badge ${getConfidenceBadgeClass(structured.confidence)}`}>
              {structured.confidence} confidence
            </span>
          )}
        </div>

        <div className="animal-inputs">
          <div className="animal-input-group">
            <label htmlFor="mice-count">Mice</label>
            <input
              id="mice-count"
              type="number"
              min="0"
              value={miceCount}
              onChange={(e) => setMiceCount(Math.max(0, parseInt(e.target.value) || 0))}
            />
          </div>

          <div className="animal-input-group">
            <label htmlFor="rats-count">Rats</label>
            <input
              id="rats-count"
              type="number"
              min="0"
              value={ratsCount}
              onChange={(e) => setRatsCount(Math.max(0, parseInt(e.target.value) || 0))}
            />
          </div>

          <div className="animal-input-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={otherSpecies}
                onChange={(e) => setOtherSpecies(e.target.checked)}
              />
              Other species used
            </label>
          </div>
        </div>

        {/* Reasoning */}
        {structured?.reasoning && (
          <div className="reasoning-box">
            <strong>How these numbers were calculated:</strong>
            <p>{structured.reasoning}</p>
          </div>
        )}

        {/* Assumptions */}
        {structured?.assumptions && structured.assumptions.length > 0 && (
          <div className="assumptions-box">
            <strong>Assumptions:</strong>
            <ul>
              {structured.assumptions.map((assumption, idx) => (
                <li key={idx}>{assumption}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Confidence warning */}
        {structured?.confidence === 'low' && (
          <div className="confidence-warning">
            <strong>Note:</strong> Confidence is low because the experimental design lacks specific details.
            Consider providing pilot data or explicit effect size estimates for more accurate calculations.
          </div>
        )}
      </div>

      {/* Parse error fallback notice */}
      {parseError && (
        <div className="parse-error-notice">
          <strong>Note:</strong> {parseError} You can still edit the justification text below.
        </div>
      )}

      {/* Justification Text */}
      <div className="justification-section">
        <div className="an-section-header">
          <h4>Justification Text (Q4.3)</h4>
        </div>
        <p className="section-note">
          This text will be saved to your protocol. Edit as needed.
        </p>
        <textarea
          value={justificationText}
          onChange={(e) => setJustificationText(e.target.value)}
          rows={8}
          placeholder="Enter justification for animal numbers..."
        />
      </div>

      {/* Reconciliation Section - shown when numbers differ from original */}
      {(showReconciliation || numbersChanged) && (
        <div className="reconciliation-section">
          <div className="an-section-header">
            <h4>Update Experimental Design?</h4>
          </div>
          <div className="reconciliation-notice">
            <p>
              Your experimental design (Q4.2) specifies <strong>{originalNumbers} animals</strong>,
              but the current count is <strong>{currentTotal} animals</strong>.
            </p>
            <p>Would you like to add a note to your experimental design explaining this change?</p>
          </div>

          <div className="reconciliation-options">
            <label className="reconciliation-checkbox">
              <input
                type="checkbox"
                checked={includeReconciliation}
                onChange={(e) => setIncludeReconciliation(e.target.checked)}
              />
              Add reconciliation note to Q4.2
            </label>
          </div>

          {includeReconciliation && (
            <div className="reconciliation-note-editor">
              <label>Note to append to Experimental Design:</label>
              <textarea
                value={reconciliationNote}
                onChange={(e) => setReconciliationNote(e.target.value)}
                rows={4}
                placeholder="Enter note to add to experimental design..."
              />
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="animal-numbers-actions">
        <button
          className="accept-button"
          onClick={handleAccept}
          disabled={!justificationText.trim()}
        >
          Accept & Continue
        </button>
        <button
          className="regenerate-button"
          onClick={() => {
            setIsLoading(true);
            generateAnalysis();
          }}
        >
          Regenerate
        </button>
      </div>
    </div>
  );
};

export default GuidedAnimalNumbers;
